import { normalizePhoneNumber, type PhoneCodeOption } from "./account-input-model"

export const PHONE_CODE_OPTIONS_STORAGE_KEY = "smm-auth-widget:phone-code-options:v2"

type PhoneCountryCode = {
  phone_code: string
  country_code?: string
  english_name?: string
  chinese_name?: string
  is_current?: number
}

export type PhoneCodeProtocol = {
  code: number
  data: PhoneCountryCode[] | null
  msg?: string
}

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">

type SessionStorageOwner = {
  readonly sessionStorage: StorageLike
}

type PhoneCodeRequest = () => Promise<PhoneCodeProtocol | null>

type IdleScheduler = {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
  cancelIdleCallback?: (handle: number) => void
  setTimeout: typeof setTimeout
  clearTimeout: typeof clearTimeout
}

const isPhoneCodeOption = (value: unknown): value is PhoneCodeOption => {
  if (!value || typeof value !== "object") return false
  const option = value as Record<string, unknown>
  return (
    typeof option.value === "string" &&
    /^\+\d+$/.test(option.value) &&
    typeof option.label === "string" &&
    option.label.length > 0 &&
    (option.searchText === undefined || typeof option.searchText === "string") &&
    (option.isCurrent === undefined || typeof option.isCurrent === "boolean")
  )
}

export const parsePhoneCodeOptionsCache = (raw: string | null): PhoneCodeOption[] | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { version?: unknown; options?: unknown }
    if (parsed.version !== 2 || !Array.isArray(parsed.options) || !parsed.options.length) return null
    return parsed.options.every(isPhoneCodeOption) ? parsed.options : null
  } catch {
    return null
  }
}

export const getPhoneCodeOptionsStorage = (owner: SessionStorageOwner | undefined): StorageLike | undefined => {
  if (!owner) return undefined
  try {
    return owner.sessionStorage
  } catch {
    return undefined
  }
}

export const readPhoneCodeOptionsCache = (storage: StorageLike | undefined): PhoneCodeOption[] | null => {
  if (!storage) return null
  let raw: string | null
  try {
    raw = storage.getItem(PHONE_CODE_OPTIONS_STORAGE_KEY)
  } catch {
    return null
  }

  const options = parsePhoneCodeOptionsCache(raw)
  if (raw !== null && !options) {
    try {
      storage.removeItem(PHONE_CODE_OPTIONS_STORAGE_KEY)
    } catch {
      // Removing invalid cache is best-effort and must not block a fresh request.
    }
  }
  return options
}

const writePhoneCodeOptionsCache = (storage: StorageLike | undefined, options: PhoneCodeOption[]): void => {
  if (!storage) return
  try {
    storage.setItem(PHONE_CODE_OPTIONS_STORAGE_KEY, JSON.stringify({ version: 2, options }))
  } catch {
    // Storage can be unavailable or full. The current request still succeeds.
  }
}

export const createPhoneCodeOptions = (codeList: readonly PhoneCountryCode[]): PhoneCodeOption[] => {
  const options = new Map<string, { label: string; searchParts: Set<string>; isCurrent: boolean }>()

  for (const item of codeList) {
    const digits = normalizePhoneNumber(item.phone_code)
    if (!digits) continue
    const value = `+${digits}`
    const current = options.get(value) ?? { label: value, searchParts: new Set<string>(), isCurrent: false }
    if (item.is_current === 1) current.isCurrent = true
    ;[value, item.country_code, item.english_name, item.chinese_name].forEach((part) => {
      if (part) current.searchParts.add(part)
    })
    options.set(value, current)
  }

  return Array.from(options, ([value, option]) => ({
    value,
    label: option.label,
    searchText: Array.from(option.searchParts).join(" "),
    isCurrent: option.isCurrent,
  }))
}

let pendingRequest: Promise<PhoneCodeOption[]> | null = null

export const loadPhoneCodeOptions = ({
  request,
  storage,
}: {
  request: PhoneCodeRequest
  storage?: StorageLike
}): Promise<PhoneCodeOption[]> => {
  const cached = readPhoneCodeOptionsCache(storage)
  if (cached) return Promise.resolve(cached)
  if (pendingRequest) return pendingRequest

  const requestPromise = request()
    .then((response) => {
      if (!response || response.code !== 0 || !response.data) {
        throw new Error(response?.msg || "Unable to load phone codes")
      }
      const options = createPhoneCodeOptions(response.data)
      if (!options.length) throw new Error("Unable to load phone codes")
      writePhoneCodeOptionsCache(storage, options)
      return options
    })
    .finally(() => {
      if (pendingRequest === requestPromise) pendingRequest = null
    })

  pendingRequest = requestPromise
  return requestPromise
}

export const schedulePhoneCodePrefetch = (run: () => void, scheduler: IdleScheduler): (() => void) => {
  if (scheduler.requestIdleCallback) {
    const handle = scheduler.requestIdleCallback(run, { timeout: 1500 })
    return () => scheduler.cancelIdleCallback?.(handle)
  }

  const handle = scheduler.setTimeout(run, 300)
  return () => scheduler.clearTimeout(handle)
}

export const initializePhoneCodeOptions = ({
  storage,
  onCacheHit,
  run,
  scheduler,
}: {
  storage: StorageLike | undefined
  onCacheHit: (options: PhoneCodeOption[]) => void
  run: () => void
  scheduler: IdleScheduler
}): (() => void) | undefined => {
  const cachedOptions = readPhoneCodeOptionsCache(storage)
  if (cachedOptions) {
    onCacheHit(cachedOptions)
    return undefined
  }
  return schedulePhoneCodePrefetch(run, scheduler)
}
